import React, { useEffect } from "react";
import { HeaderSection, MainContainer, Partition } from "../index.styled";
import { ReactComponent as FacilitiesFilter } from "../../../Assets/Icons/FacilitiesFilter.svg";
import styled from "styled-components";
import CustomSlider from "../../CustomSlider";
import { viewSizeCalculator } from "../../../utils";
import { facilityTypes, ownershipTypes } from "../../../Assets/MockData";
import CustomInput from "../../Input";
import Info from "../../Info";

const Facilities = ({
  data,
  reference,
  filteredData,
  handleInputChange,
  suggestionsList,
  suggestionsLoader,
  onFieldSuggestionsChange,
  onReset,
  matchedHospital,
  isFetching,
}) => {
  const fields = data.fields;

  const getFilteredSuggestions = (input, suggestions) => {
    if (!Array.isArray(suggestions)) return [];
    if (typeof input !== "string") return suggestions;
    const lowerInput = input.toLowerCase();
    return suggestions.filter((item) =>
      item?.toLowerCase().includes(lowerInput),
    );
  };


  const autoFillField = (fieldName, suggestions) => {
    if (suggestions?.length === 1) {
      handleInputChange(fieldName, suggestions[0]);
    }
    onFieldSuggestionsChange?.(fieldName, suggestions);
  };


  // Add reset handler
  const handleReset = () => {
    // Reset facility type
    handleInputChange("facility_type", "");
    // Reset ownership type
    handleInputChange("prov_control_type", "");
    // Reset bed count
    handleInputChange("bed_count_min", "");
    handleInputChange("bed_count_max", "");
    // Reset square footage
    handleInputChange("square_footage_min", "");
    handleInputChange("square_footage_max", "");
    // Reset patient discharge
    handleInputChange("patient_discharge_min", "");
    handleInputChange("patient_discharge_max", "");


    // Call parent reset handler if provided
    if (onReset) {
      onReset();
    }
  };

  useEffect(() => {
    if (!matchedHospital) return;


    // Update facility_type if it's empty or doesn't match
    if (
      matchedHospital.fac_type &&
      (!filteredData.facility_type ||
        !matchedHospital.fac_type
          .toLowerCase()
          .includes(filteredData.facility_type.toLowerCase()) &&
        !filteredData.facility_type
          .toLowerCase()
          .includes(matchedHospital.fac_type.toLowerCase()))
    ) {
      handleInputChange("facility_type", matchedHospital.fac_type);
    }


    // Update prov_control_type if it's empty
    if (
      matchedHospital.prov_control_type &&
      (!filteredData.prov_control_type ||
        filteredData.prov_control_type !== matchedHospital.prov_control_type)
    ) {
      handleInputChange("prov_control_type", matchedHospital.prov_control_type);
    }
  }, [matchedHospital, filteredData, handleInputChange]);

  return (
    <MainContainer ref={reference}>
      <HeaderSection>
        <FacilitiesFilter />
        {data.category}
      </HeaderSection>
      <ContentWrapper>
        <RowContainer width={"70%"} gap={1.25}>
          <div style={{
            width: "48.75%",
            opacity: isFetching ? 0.7 : !!filteredData.hospital_name ? 1 : 1,
          }}>
            <Info
              showArrow={false}
              config={{
                tooltipText: isFetching
                  ? "Bear with us we're fetching the data"
                  : !!filteredData.hospital_name
                    ? "Can't edit as a Hospital has already been selected"
                    : null,
                position: "top",
                offsetY: -10,
              }}
            >
              <CustomInput
                dropdown={fields[0].type}
                comboMode={true}
                disabled={!!filteredData.hospital_name}
                placeholder={fields[0].placeholder}
                name="facility_type"
                label={"Facility Type"}
                value={filteredData.facility_type || ""}
                onChange={handleInputChange}
                height={2.2}
                suggestions={facilityTypes}
                enabledOptions={suggestionsList?.fac_type}
                suggestionLoader={suggestionsLoader.cbsa}
                onInputChange={(value) => {
                  const filtered = getFilteredSuggestions(
                    value,
                    suggestionsList?.fac_type || facilityTypes,
                  );
                  autoFillField("facility_type", filtered);
                }}
                onReset={handleReset}
              />
            </Info>
          </div>
          <div style={{
            width: "48.75%",
            opacity: isFetching ? 0.7 : !!filteredData.hospital_name ? 1 : 1,
          }}>
            <Info
              showArrow={false}
              config={{
                tooltipText: isFetching
                  ? "Bear with us we're fetching the data"
                  : !!filteredData.hospital_name
                    ? "Can't edit as a Hospital has already been selected"
                    : null,
                position: "top",
                offsetY: -10,
              }}
            >
              <CustomInput
                dropdown={fields[1].type}
                comboMode={true}
                disabled={!!filteredData.hospital_name}
                placeholder={fields[1].placeholder}
                name="prov_control_type"
                label={"Ownership Type"}
                value={filteredData.prov_control_type || ""}
                onChange={handleInputChange}
                height={2.2}
                suggestions={ownershipTypes}
                enabledOptions={suggestionsList?.prov_control_type}
                onInputChange={(value) => {
                  const filtered = getFilteredSuggestions(
                    value,
                    suggestionsList?.prov_control_type || ownershipTypes,
                  );
                  autoFillField("prov_control_type", filtered);
                }}
                onReset={handleReset}
              />
            </Info>
          </div>
        </RowContainer>
        <RowContainer wrap={false} gap={5.6}>
          <div style={{
            width: "51%",
            opacity: isFetching ? 0.7 : !!filteredData.hospital_name ? 1 : 1,
          }}>
            <Info
              showArrow={false}
              config={{
                tooltipText: isFetching
                  ? "Bear with us we're fetching the data"
                  : !!filteredData.hospital_name
                    ? "Can't edit as a Hospital has already been selected"
                    : null,
                position: "top",
                offsetY: -10,
              }}
            >
              <CustomSlider
                title="Bed Count"
                onChange={handleInputChange}
                minValue={filteredData.bed_count_min || ""}
                maxValue={filteredData.bed_count_max || ""}
                showMinMaxFields={true}
                minValueName="bed_count_min"
                maxValueName="bed_count_max"
                filteredData={filteredData}
                step={1}
                disabled={filteredData.hospital_name}
              />
            </Info>
          </div>
          <div style={{
            width: "51%",
            opacity: isFetching ? 0.7 : !!filteredData.hospital_name ? 1 : 1,
          }}>
            <Info
              showArrow={false}
              config={{
                tooltipText: isFetching
                  ? "Bear with us we're fetching the data"
                  : !!filteredData.hospital_name
                    ? "Can't edit as a Hospital has already been selected"
                    : null,
                position: "top",
                offsetY: -10,
              }}
            >
              <CustomSlider
                title="Square Footage"
                onChange={handleInputChange}
                minValue={filteredData.square_footage_min || ""}
                maxValue={filteredData.square_footage_max || ""}
                showMinMaxFields={true}
                minValueName="square_footage_min"
                maxValueName="square_footage_max"
                filteredData={filteredData}
                step={1}
                disabled={filteredData.hospital_name}
              />
            </Info>
          </div>
          <div style={{
            width: "51%",
            opacity: isFetching ? 0.7 : !!filteredData.hospital_name ? 1 : 1,
          }}>
            <Info
              showArrow={false}
              config={{
                tooltipText: isFetching
                  ? "Bear with us we're fetching the data"
                  : !!filteredData.hospital_name
                    ? "Can't edit as a Hospital has already been selected"
                    : null,
                position: "top",
                offsetY: -10,
              }}
            >
              <CustomSlider
                title="Patient Discharge Volume"
                onChange={handleInputChange}
                minValue={filteredData.patient_discharge_min || ""}
                maxValue={filteredData.patient_discharge_max || ""}
                showMinMaxFields={true}
                minValueName="patient_discharge_min"
                maxValueName="patient_discharge_max"
                filteredData={filteredData}
                step={1}
                disabled={filteredData.hospital_name}
              />
            </Info>
          </div>
        </RowContainer>
      </ContentWrapper>
    </MainContainer>
  );
};

export default Facilities;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const RowContainer = styled.div`
  width: ${({ width }) => width || "100%"};
  display: flex; 
  flex-wrap: ${({ wrap }) => (wrap ? "wrap" : "no-wrap")};
  gap: ${({ gap }) => `${viewSizeCalculator(gap)}`};
`;